// src/components/SentimentListView.js
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatDate, formatDateTime } from '../utils';
import CallEvalDisplay from './CallEvalDisplay'; // Adjust path if needed

export const entityPathSegment = (type) => {
    const normalized = (type || '').toLowerCase().replace(/\s+/g, '');
    if (normalized === 'goal') return 'goals';
    if (normalized === 'initiative') return 'initiatives';
    if (normalized === 'issue') return 'issues';
    if (normalized === 'actionitem' || normalized === 'action-item') return 'action-items';
    return null;
};

export const CommunicationCard = ({ comm, clientId, departmentId, navigate }) => {
    const evidenced = Array.isArray(comm.evidencedEntities) ? comm.evidencedEntities : [];
    const handleEntityClick = (entity) => {
        const segment = entityPathSegment(entity.type);
        if (!segment || !entity.id) return;
        const base = clientId ? `/accounts/${clientId}` : `/operations/${departmentId}`;
        navigate(`${base}/${segment}/${entity.id}`);
    };
    return (
        <article
            data-testid="comm-card"
            className="rounded-xl p-4"
            style={{
                backgroundColor: 'var(--pallium-white)',
                border: '1px solid var(--pallium-stroke-light)',
                boxShadow: 'var(--pallium-shadow-sm)',
            }}
        >
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 mb-3">
                <div>
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span
                            className="px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide"
                            style={{ backgroundColor: 'var(--pallium-purple-0-5)', color: 'var(--pallium-primary-purple)' }}
                        >
                            {comm.type || comm.record_type || 'comm'}
                        </span>
                        {comm.staff && (
                            <span className="text-xs" style={{ color: 'var(--pallium-text-tertiary)' }}>
                                {comm.staff}
                            </span>
                        )}
                    </div>
                    <h3 className="text-base font-semibold leading-snug" style={{ color: 'var(--pallium-text-primary)' }}>
                        {comm.title || 'Untitled communication'}
                    </h3>
                </div>
                <span className="text-xs whitespace-nowrap" style={{ color: 'var(--pallium-text-tertiary)' }}>
                    {formatDateTime(comm.date)}
                </span>
            </div>

            {comm.summary && (
                <p className="text-sm leading-relaxed mb-3 whitespace-pre-line" style={{ color: 'var(--pallium-text-secondary)' }}>
                    {comm.summary}
                </p>
            )}

            {evidenced.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                    {evidenced.slice(0, 8).filter(entity => entity?.id && entity?.name).map((entity, idx) => (
                        <button
                            type="button"
                            key={`${entity.id || entity.name || idx}`}
                            onClick={() => handleEntityClick(entity)}
                            className="px-2 py-1 text-xs rounded-full hover:underline"
                            style={{ backgroundColor: 'var(--pallium-stroke-lighter)', color: 'var(--pallium-primary-purple)' }}
                        >
                            {entity.name || entity.id} {entity.type ? `(${entity.type})` : ''}
                        </button>
                    ))}
                </div>
            )}

            {Array.isArray(comm.callEvaluations) && comm.callEvaluations.length > 0 && (
                <div className="pt-3 mt-3" style={{ borderTop: '1px solid var(--pallium-stroke-light)' }}>
                    <CallEvalDisplay evaluations={comm.callEvaluations} />
                </div>
            )}
        </article>
    );
};

const INITIAL_COMMS_LIMIT = 50;
const COMMS_PAGE_SIZE = 50;

// --- Component ---
const SentimentListView = () => {
    const { apiService, clientId, dateRange } = useOutletContext();
    const navigate = useNavigate();

    // State for Communications List
    const [allCommunicationsList, setAllCommunicationsList] = useState([]);
    const [loadingAllComms, setLoadingAllComms] = useState(false);
    const [errorAllComms, setErrorAllComms] = useState(null);
    const [commsLimit, setCommsLimit] = useState(INITIAL_COMMS_LIMIT);

    // State for Sentiment Chart
    const [sentimentData, setSentimentData] = useState([]);
    const [loadingSentimentChart, setLoadingSentimentChart] = useState(false);

    // Load Communications List (adapted from original Dashboard.js)
    const loadAllCommunications = useCallback(async () => {
        if (!apiService || !clientId) return;
        setLoadingAllComms(true); setErrorAllComms(null);
        try {
            const comms = await apiService.getAllCommunicationsList(clientId, dateRange?.start, dateRange?.end, commsLimit);
            setAllCommunicationsList(comms);
        } catch (e) { setErrorAllComms(e.message); setAllCommunicationsList([]); }
        finally { setLoadingAllComms(false); }
    }, [apiService, clientId, dateRange, commsLimit]);

     // Load Sentiment Chart Data
      const loadSentimentChartData = useCallback(async () => {
          if (!apiService || !clientId) return;
          setLoadingSentimentChart(true);
          try {
              const sentimentResult = await apiService.getSentimentOverTime(clientId);
              setSentimentData(sentimentResult?.map(item => ({
                    ...item,
                    day: formatDate(item.day) // Ensure date is formatted for XAxis
              })) ?? []);
          } catch (err) { console.error("Error loading sentiment chart data:", err); setSentimentData([]); }
          finally { setLoadingSentimentChart(false); }
      }, [apiService, clientId]);

    useEffect(() => {
        setCommsLimit(INITIAL_COMMS_LIMIT);
    }, [clientId, dateRange]);

    // useEffect to load data when component mounts or clientId changes
    useEffect(() => {
        loadAllCommunications();
        loadSentimentChartData();
    }, [loadAllCommunications, loadSentimentChartData]); // Depend on the load functions


    return (
        <>
            <h2 className="text-xl font-semibold mb-6">Communications & Sentiment</h2>

            {/* Chart */}
            <div className="bg-white rounded-lg shadow p-4 md:p-6 mb-6 md:mb-8">
                 <h3 className="text-lg font-medium text-gray-900 mb-4">Sentiment Trend</h3>
                 {loadingSentimentChart ? <p>Loading chart...</p> : sentimentData.length > 0 ? (
                     <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={sentimentData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="day" tick={{fontSize: 10}} />
                            <YAxis domain={[0, 1]} tickFormatter={(value) => `${(value * 100).toFixed(0)}%`} tick={{fontSize: 10}} />
                            <Tooltip wrapperClassName="text-xs" formatter={(value) => [`${(value * 100).toFixed(1)}%`, 'Sentiment']} labelFormatter={(value) => `Date: ${value}`} />
                            <Line type="monotone" dataKey="avgSentiment" stroke="#3B82F6" strokeWidth={2} dot={{r: 3}} activeDot={{r: 5}} name="Avg Sentiment" />
                        </LineChart>
                      </ResponsiveContainer>
                  ) : (
                      <p className="text-center text-sm text-gray-500 py-4">No sentiment data available.</p>
                  )}
             </div>

            {/* List */}
            <div
                className="rounded-xl p-4 md:p-6"
                style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
            >
                <h3 className="text-lg font-medium mb-1" style={{ color: 'var(--pallium-text-primary)' }}>
                    All Communications
                </h3>
                <p className="text-sm mb-4" style={{ color: 'var(--pallium-text-secondary)' }}>
                    Chronological communication summaries with linked entities and usable call scores.
                </p>
                {loadingAllComms && <div className="text-sm" style={{ color: 'var(--pallium-text-secondary)' }}>Loading communications...</div>}
                {errorAllComms && <div className="text-sm" style={{ color: 'var(--pallium-error-red)' }}>Error loading communications: {errorAllComms}</div>}
                {!loadingAllComms && !errorAllComms && allCommunicationsList.length === 0 && (
                    <div className="text-sm" style={{ color: 'var(--pallium-text-secondary)' }}>No communications found.</div>
                )}
                {!loadingAllComms && !errorAllComms && allCommunicationsList.length > 0 && (
                    <>
                        <div className="space-y-4">
                            {allCommunicationsList.map((comm) => (
                                <CommunicationCard key={comm.id || comm.record_id || `${comm.date}-${comm.title}`} comm={comm} clientId={clientId} navigate={navigate} />
                            ))}
                        </div>
                        {allCommunicationsList.length >= commsLimit && (
                            <div className="mt-4 flex justify-center">
                                <button
                                    type="button"
                                    onClick={() => setCommsLimit((limit) => limit + COMMS_PAGE_SIZE)}
                                    className="px-4 py-2 rounded-lg text-sm font-medium"
                                    style={{ backgroundColor: 'var(--pallium-purple-0-5)', color: 'var(--pallium-primary-purple)' }}
                                    disabled={loadingAllComms}
                                >
                                    Load more communications
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default SentimentListView;